import { useEffect, useState } from 'react'
import type { AgentToolCall, SendMessagePayloadWithApproval, StreamChunk } from '@multiplayer-app/ai-agent-types'
import { MessageRole, StreamChunkType } from '@multiplayer-app/ai-agent-types'
import { useAgentRuntime, useAgentStore } from '@/context/AgentProvider'
import { UiChatStatus } from '@/state/useAgentStore'
import type { ToolApprovalButtonsProps } from '../types'
import { ApprovalStatusInline } from './ApprovalStatusInline'
import { IconButton } from '@/components/ui/IconButton'
import { ToolUserActionForm } from './ToolUserActionForm'

export const ToolApprovalButtons = ({ call, messageId, chatId, onApprovalSent }: ToolApprovalButtonsProps) => {
  const runtime = useAgentRuntime()
  const toolLabel = runtime.tools.get(call.name)?.label ?? call.name

  const appendMessagesToChat = useAgentStore((state) => state.appendMessagesToChat)
  const setChatMessages = useAgentStore((state) => state.setChatMessages)
  const setChatStatus = useAgentStore((state) => state.setChatStatus)
  const setPendingAssistant = useAgentStore((state) => state.setPendingAssistant)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isEditingInput, setIsEditingInput] = useState(false)
  const [isSavingInput, setIsSavingInput] = useState(false)
  const [editableInput, setEditableInput] = useState<string>(JSON.stringify(call.input ?? {}, null, 2))
  const [inputError, setInputError] = useState<string | undefined>(undefined)
  const userId = runtime.config.user?.id ?? 'guest'
  const requiresUserAction = call.requiresUserAction

  useEffect(() => {
    if (!isEditingInput) {
      setEditableInput(JSON.stringify(call.input ?? {}, null, 2))
      setInputError(undefined)
    }
  }, [call.id, call.input, isEditingInput])

  const handleStartEdit = () => {
    setEditableInput(JSON.stringify(call.input ?? {}, null, 2))
    setInputError(undefined)
    setIsEditingInput(true)
  }

  const handleCancelEdit = () => {
    setEditableInput(JSON.stringify(call.input ?? {}, null, 2))
    setInputError(undefined)
    setIsEditingInput(false)
  }

  const handleSaveInput = async () => {
    if (!runtime.transport.updateToolCall || isSavingInput || isSubmitting) return
    setInputError(undefined)
    setIsSavingInput(true)
    try {
      const parsed = JSON.parse(editableInput)
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Tool input must be a JSON object.')
      }
      const updatedMessage = await runtime.transport.updateToolCall({
        chatId,
        messageId,
        toolCallId: call.id,
        input: parsed as Record<string, unknown>
      })
      appendMessagesToChat(chatId, [updatedMessage])
      setIsEditingInput(false)
    } catch (error) {
      setInputError(error instanceof Error ? error.message : 'Failed to update tool input.')
    } finally {
      setIsSavingInput(false)
    }
  }

  const patchToolCall = (patch: Partial<AgentToolCall>) => {
    setChatMessages(chatId, (prev) =>
      prev.map((message) =>
        message.id === messageId
          ? {
              ...message,
              toolCalls: (message.toolCalls ?? []).map((tc) => (tc.id === call.id ? { ...tc, ...patch } : tc))
            }
          : message
      )
    )
  }

  const handleApproval = async (approved: boolean, userResponse?: string) => {
    if (!call.approvalId || isSubmitting || isSavingInput || isEditingInput) return

    setIsSubmitting(true)
    // Optimistic: show the answer and a processing state right away.
    // The server confirms via stream chunks / socket events; on failure we revert.
    patchToolCall({ approved, ...(userResponse ? { userResponse } : {}) })
    setChatStatus(chatId, UiChatStatus.Streaming)
    // Typing placeholder until the continuation message starts streaming in.
    setPendingAssistant(chatId, {
      id: `pending-approval-${call.id}`,
      chat: chatId,
      role: MessageRole.Assistant,
      content: '',
      reasoning: '',
      toolCalls: [],
      attachments: [],
      createdAt: new Date().toISOString()
    })
    try {
      const payload: SendMessagePayloadWithApproval = {
        chatId,
        messageId,
        approvalId: call.approvalId,
        approved,
        ...(userResponse ? { userResponse } : {}),
        userId
      }

      const updatedChat = await runtime.transport.streamMessage(
        payload,
        (chunk: StreamChunk) => {
          // Update chat in real-time as chunks arrive
          if (chunk.type === StreamChunkType.Message && chunk.message) {
            // The continuation streams into a new assistant message; once it shows
            // up, the typing placeholder is no longer needed.
            if (chunk.message.id !== messageId) {
              setPendingAssistant(chatId, undefined)
            }
            appendMessagesToChat(chatId, [chunk.message!])
          }
        },
        { streamKey: chatId }
      )

      // Final update: merge messages into existing chat (metadata already in store)
      if (updatedChat) {
        appendMessagesToChat(updatedChat.id, updatedChat.messages)
      }
      // Refresh chat so its persisted status drives the UI status (idle, waiting, …).
      await onApprovalSent()
    } catch (error) {
      console.error('Failed to send approval:', error)
      patchToolCall({ approved: undefined, userResponse: undefined })
      setChatStatus(chatId, UiChatStatus.Idle)
    } finally {
      setPendingAssistant(chatId, undefined)
      setIsSubmitting(false)
    }
  }

  if (call.approved !== undefined) {
    return <ApprovalStatusInline call={call} />
  }

  if (requiresUserAction) {
    return (
      <ToolUserActionForm
        callId={call.id}
        input={call.input}
        isSubmitting={isSubmitting}
        onSubmit={(userResponse) => handleApproval(true, userResponse)}
      />
    )
  }

  return (
    <div className='mp-agent-tool-approval-buttons'>
      <div className='mp-agent-tool-approval-request-message'>Agent wants to execute tool {toolLabel}.</div>
      <div className='mp-agent-tool-input-card'>
        <div className='mp-agent-tool-input-card-header'>
          <span className='mp-agent-tool-input-card-title'>Tool input</span>
          {!isEditingInput ? (
            <IconButton
              icon='Pencil'
              size='sm'
              tooltip='Edit tool input'
              onClick={handleStartEdit}
              disabled={isSubmitting || isSavingInput}
              aria-label='Edit tool input'
            />
          ) : (
            <div className='mp-agent-tool-input-actions'>
              <IconButton
                icon='Check'
                size='sm'
                tooltip='Save tool input'
                onClick={handleSaveInput}
                disabled={isSavingInput || isSubmitting}
                aria-label='Save tool input'
              />
              <IconButton
                icon='X'
                size='sm'
                tooltip='Cancel input edit'
                onClick={handleCancelEdit}
                disabled={isSavingInput || isSubmitting}
                aria-label='Cancel tool input edit'
              />
            </div>
          )}
        </div>
        {isEditingInput ? (
          <textarea
            className='mp-agent-tool-input-editor'
            value={editableInput}
            onChange={(event) => {
              setEditableInput(event.target.value)
              if (inputError) setInputError(undefined)
            }}
            rows={8}
            spellCheck={false}
            disabled={isSavingInput || isSubmitting}
          />
        ) : (
          <pre className='mp-agent-tool-input-preview'>{JSON.stringify(call.input ?? {}, null, 2)}</pre>
        )}
        {inputError && <div className='mp-agent-tool-input-error'>{inputError}</div>}
      </div>
      <div className='mp-agent-tool-approval-actions'>
        <button
          className='mp-agent-tool-approve-button'
          onClick={() => handleApproval(true)}
          disabled={isSubmitting || isEditingInput || isSavingInput}
        >
          {isSubmitting ? 'Processing...' : 'Approve'}
        </button>
        <button className='mp-agent-tool-deny-button' onClick={() => handleApproval(false)} disabled={isSubmitting || isSavingInput}>
          {isSubmitting ? 'Processing...' : 'Deny'}
        </button>
      </div>
    </div>
  )
}
